import axios, { AxiosInstance, InternalAxiosRequestConfig, AxiosError } from 'axios'

const API_URL = import.meta.env.VITE_API_URL || ''

const api: AxiosInstance = axios.create({
  baseURL: `${API_URL}/api/v1`,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30000,
})

// ─── Intercepteur requête : injecter le Bearer Token ─────────────────────────
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('accessToken')
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error),
)

// ─── Flag anti-boucle de refresh ─────────────────────────────────────────────
let isRefreshing = false
let failedQueue: Array<{
  resolve: (token: string) => void
  reject: (error: any) => void
}> = []

function processQueue(error: any, token: string | null = null) {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error)
    else resolve(token!)
  })
  failedQueue = []
}

// ─── Reconnexion socket après refresh ────────────────────────────────────────
// Import dynamique pour éviter la dépendance circulaire axios ↔ socket
async function reconnectSocket(token: string): Promise<void> {
  try {
    const { reconnectSocketWithToken } = await import('./socket')
    reconnectSocketWithToken(token)
  } catch {
    // Module socket non disponible — ignorer silencieusement
  }
}

// ─── Logout propre — reset store Zustand + tokens + redirect ─────────────────
// Import dynamique pour éviter la dépendance circulaire axios ↔ store
function forceLogout() {
  // 1. Vider les tokens localStorage
  clearTokens()
  // 2. Reset le store Zustand directement (import dynamique)
  import('@/stores/auth.store').then(({ useAuthStore }) => {
    useAuthStore.getState().logout()
  }).catch(() => {
    // Fallback si l'import échoue
  }).finally(() => {
    // 3. Rediriger — seulement si pas déjà sur /login
    if (!window.location.pathname.includes('/login')) {
      window.location.href = '/login'
    }
  })
}

// ─── Intercepteur réponse : refresh automatique sur 401 ──────────────────────
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }

    // Ignorer les 401 sur la route de refresh elle-même (évite boucle infinie)
    const isRefreshEndpoint = originalRequest?.url?.includes('/auth/refresh')

    if (error.response?.status === 401 && !originalRequest._retry && !isRefreshEndpoint) {
      const refreshToken = localStorage.getItem('refreshToken')

      // Pas de refresh token → logout propre
      if (!refreshToken) {
        forceLogout()
        return Promise.reject(error)
      }

      // Requête déjà en cours de refresh → mettre en queue
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`
          return api(originalRequest)
        }).catch((err) => Promise.reject(err))
      }

      originalRequest._retry = true
      isRefreshing = true

      try {
        const response = await axios.post(`${API_URL}/api/v1/auth/refresh`, {
          refreshToken,
        })

        const { accessToken, refreshToken: newRefreshToken } = response.data.data
        setTokens(accessToken, newRefreshToken)
        processQueue(null, accessToken)

        // ── Reconnecter le socket WebSocket avec le nouveau token ──────────
        // Sans ça, le socket garde l'ancien token expiré et reste en 401 WS
        reconnectSocket(accessToken).catch(() => {})

        originalRequest.headers.Authorization = `Bearer ${accessToken}`
        return api(originalRequest)
      } catch (refreshError) {
        // Refresh token invalide ou expiré → logout propre
        processQueue(refreshError, null)
        forceLogout()
        return Promise.reject(refreshError)
      } finally {
        isRefreshing = false
      }
    }

    return Promise.reject(error)
  },
)

export function setTokens(accessToken: string, refreshToken: string) {
  localStorage.setItem('accessToken', accessToken)
  localStorage.setItem('refreshToken', refreshToken)
}

export function clearTokens() {
  localStorage.removeItem('accessToken')
  localStorage.removeItem('refreshToken')
}

export function getAccessToken(): string | null {
  return localStorage.getItem('accessToken')
}

export default api
